import React, { useState } from "react";
import { Notice } from "./ui";

/**
 * Pick the prizes a society actually plays.
 *
 * The catalogue is here to fill the form quickly, not to limit anything —
 * a prize is four fields, and anything this can't describe can still be
 * added by hand afterwards.
 */

const CATALOGUE = [
  { id: "sf_front",  label: "front nine",            kind: "stableford",  holes: "front9",
    blurb: "Most Stableford points over the first nine" },
  { id: "sf_back",   label: "back nine",             kind: "stableford",  holes: "back9",
    blurb: "Most points over the last nine" },
  { id: "sf_all",    label: "overall",               kind: "stableford",  holes: "all",
    blurb: "Most points over the whole card" },
  { id: "nett_p3",   label: "best nett on the par 3s", kind: "nett",      holes: "par3",
    blurb: "Fewest nett strokes on the short holes" },
  { id: "nett_p5",   label: "best nett on the par 5s", kind: "nett",      holes: "par5",
    blurb: "Fewest nett strokes on the long holes" },
  { id: "nett_all",  label: "best nett",             kind: "nett",        holes: "all",
    blurb: "Fewest nett strokes over the round" },
  { id: "gross_all", label: "best gross",            kind: "gross",       holes: "all",
    blurb: "Fewest strokes, no handicap" },
  { id: "birdies",   label: "birdies and nett eagles", kind: "birdie_rate", holes: "all",
    blurb: "Pays for every one. A hole counts once even if it's both." },
];

export default function PrizePicker({ rounds, onAdd, busy }) {
  const [picked, setPicked] = useState({});
  const [error, setError] = useState("");

  const toggle = (id) =>
    setPicked((p) => {
      const next = { ...p };
      if (next[id]) delete next[id];
      else next[id] = { amount: "", scope: "each_round" };
      return next;
    });

  const chosen = Object.entries(picked);

  function submit(e) {
    e.preventDefault();
    setError("");
    const specs = chosen.map(([id, cfg]) => {
      const item = CATALOGUE.find((c) => c.id === id);
      return {
        kind: item.kind, holes: item.holes, label: item.label,
        amount: Number(cfg.amount) || 0, scope: cfg.scope,
      };
    });
    if (specs.length === 0) return setError("Tick at least one prize.");
    onAdd(specs, () => setPicked({}));
  }

  return (
    <form className="surface" style={{ padding: "1.1rem" }} onSubmit={submit}>
      <h2 style={{ fontSize: "1rem", marginBottom: ".3rem" }}>Choose your prizes</h2>
      <p className="muted" style={{ fontSize: ".8125rem", lineHeight: 1.6, margin: "0 0 1rem" }}>
        Tick what your society plays. "Every round" makes one prize per round,
        named after it — including birdies, which pay per round rather than
        being totted up at the end. Amounts can be left blank and set later.
      </p>

      <div className="picker">
        {CATALOGUE.map((c) => {
          const on = !!picked[c.id];
          return (
            <div className={`pick${on ? " on" : ""}`} key={c.id}>
              <label className="pick-main">
                <input type="checkbox" checked={on} onChange={() => toggle(c.id)} />
                <span>
                  <strong>{c.label.charAt(0).toUpperCase() + c.label.slice(1)}</strong>
                  <small>{c.blurb}</small>
                </span>
              </label>

              {on && (
                <div className="pick-cfg">
                  <label>
                    <span className="eyebrow">Amount</span>
                    <input className="input" inputMode="decimal" placeholder="0.00"
                           value={picked[c.id].amount}
                           onChange={(e) => setPicked((p) => ({
                             ...p, [c.id]: { ...p[c.id], amount: e.target.value } }))} />
                  </label>
                  <label>
                    <span className="eyebrow">Applies to</span>
                    <select className="select" value={picked[c.id].scope}

                            onChange={(e) => setPicked((p) => ({
                              ...p, [c.id]: { ...p[c.id], scope: e.target.value } }))}>
                      <option value="each_round">Every round</option>
                      <option value="competition">Whole competition</option>
                      {rounds.map((r) => (
                        <option key={r.id} value={r.id}>{r.name} only</option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Notice kind="error">{error}</Notice>

      <div className="row" style={{ marginTop: ".9rem" }}>
        <button className="btn btn-primary" disabled={busy || chosen.length === 0}>
          {busy ? "Adding…"
            : chosen.length === 0 ? "Add prizes"
            : `Add ${chosen.length} prize${chosen.length === 1 ? "" : "s"}`}
        </button>
        {chosen.length > 0 && (
          <button type="button" className="btn btn-ghost" onClick={() => setPicked({})}>
            Clear
          </button>
        )}
      </div>
    </form>
  );
}
